import React, { useRef, useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { Input } from '../../../../components/ui/input';
import { cn } from '../../../../components/ui';

export interface ChatInputProps {
  /**
   * Additional class name for the component
   */
  className?: string;
  
  /**
   * Current input value
   */
  value: string;
  
  /**
   * Handler for input changes
   */
  onChange: (value: string) => void;
  
  /**
   * Handler for form submission
   */
  onSubmit: () => void;
  
  /**
   * Whether the input is loading/processing
   */
  isLoading?: boolean;
  
  /**
   * Whether the user has an active subscription
   */
  hasActiveSubscription?: boolean;
  
  /**
   * Placeholder text for the input
   */
  placeholder?: {
    active: string;
    inactive: string;
  };
  
  /**
   * Whether to auto-focus the input
   */
  autoFocus?: boolean;
  
  /**
   * Function to check if an interactive element is focused
   */
  isInteractiveElementFocused?: () => boolean;
}

export function ChatInput({
  className,
  value,
  onChange,
  onSubmit,
  isLoading = false,
  hasActiveSubscription = true,
  placeholder = {
    active: "Ask Nova AI about your data...",
    inactive: "Upgrade to use Nova AI"
  },
  autoFocus = true,
  isInteractiveElementFocused = () => false
}: ChatInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const wasLoadingRef = useRef(isLoading);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow typing even when loading - users should be able to prepare their next message
    onChange(e.target.value);
  };
  
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!hasActiveSubscription || isLoading || !value.trim()) return;
    
    onSubmit();
  };

  // Focus the input when loading state changes from true to false
  useEffect(() => {
    // Check if loading state changed from true to false
    if (wasLoadingRef.current && !isLoading) {
      // Focus the input after a short delay to ensure the UI has updated
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
    
    // Update the ref for the next render
    wasLoadingRef.current = isLoading;
  }, [isLoading]);

  return (
    <form 
      onSubmit={handleSubmit}
      className={cn("relative", className)}
    >
      <div 
        className={cn(
          "flex items-center gap-2 bg-card rounded-lg px-3 py-1 shadow-sm transition-all border-2 hover:border-supernova-purple/90",
          { "opacity-70": !hasActiveSubscription },
          { "border-supernova-purple scale-[1.01] shadow-md": isFocused },
          { "bg-card/80": isLoading }
        )}
        style={{ 
          borderColor: isFocused ? 'rgba(138, 43, 226, 0.8)' : 'rgba(138, 43, 226, 0.8)',
          boxShadow: isFocused ? '0 4px 12px rgba(138, 43, 226, 0.15)' : '0 0 0 2px rgba(138, 43, 226, 0.1)'
        }}
      >
        <Sparkles className="h-4 w-4 text-supernova-purple" />
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={hasActiveSubscription 
            ? placeholder.active 
            : placeholder.inactive}
          className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-10 bg-transparent text-foreground placeholder:text-muted-foreground/70"
          autoFocus={autoFocus}
        />
        <button 
          type="submit" 
          className={cn(
            "p-2 rounded-md transition-colors",
            (!hasActiveSubscription || isLoading || !value.trim()) 
              ? "opacity-50 cursor-not-allowed" 
              : "hover:bg-supernova-purple/10 hover:text-supernova-purple"
          )}
          disabled={!hasActiveSubscription || isLoading || !value.trim()}
        >
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className={cn(
              "text-foreground transition-transform",
              isLoading ? "animate-pulse" : ""
            )}
          >
            <path 
              d="M22 2L11 13" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <path 
              d="M22 2L15 22L11 13L2 9L22 2Z" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </form>
  );
} 